import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert } from '@clay-run/glaze/alert';
import {
  CollapsibleCard,
  CollapsibleCardBody,
  CollapsibleCardHeader,
} from '@clay-run/glaze/collapsible-card';
import { LoadingSpinner } from '@clay-run/glaze/loading-spinner';
import { SegmentedControl, SegmentedControlOption } from '@clay-run/glaze/segmented-control';
import { StatusBadge } from '@clay-run/glaze/status-badge';
import { Text } from '@clay-run/glaze/text';

import { useApi } from '../../../_shared/hooks';
import type { CampaignMember } from '../../../_shared/launcher-campaign.types';
import { listPlaySendsRequest } from '../../api-client';
import { anySendLive, playSendsQueryKey } from '../../dispatch-inspector-logic';
import { DISPATCH_LOG_ANCHOR_ID, setDispatchLogView, useDispatchLogFilterStore } from '../../dispatch-log-filter-store';
import type { MemberResultFocus } from '../../member-test-store';
import { setDispatchLogOpen, useReviewCardsStore } from '../../review-cards-store';
import type { PlaybooksPlaySendsResponse } from '../../schemas';
import { sendProgressLine } from '../../send-summary';

import { PlayLogTable } from './dispatch-log/PlayLogTable';
import { SendLogCard } from './dispatch-log/SendLogCard';

/**
 * The Review step's Dispatch log (PR D, U6b; redesigned by plan
 * 2026-09-27-003, U6 — approved prototype
 * `plans/2026-09-27-002-dispatch-log-redesign-prototype.html`).
 *
 * A thin shell since the redesign. It owns:
 *   - the send-history read (`GET /plays/:id/sends`) — one small summary per
 *     send, never the rows (U0a; an open card pages its own rows) — refetched
 *     every 5s only while a summary says something could still change
 *     (`anySendLive`, the server's own `shouldPoll` verdict);
 *   - OPEN-ON-SEND: `focusSendId` (the just-created send, from `ReviewStep`)
 *     is ADDED to the open set — never reset on remount, so StrictMode's dev
 *     double-invoke is a harmless re-add (plain UI `useState`, no ref);
 *   - the card's open/closed state (`review-cards-store`, COLLAPSED by default;
 *     a fresh send, "All of Ana's sends" and the like expand it first), and a
 *     "Sending" badge + progress line in the header so a collapsed log never
 *     hides a running send.
 *
 * Everything per send and per person lives in `./dispatch-log/`:
 * `SendLogCard` (header, summary, filters, table, Retry all, the automatic
 * Clay re-check), `SendLogRow` (one person + the expanded detail), and the
 * detail panels. Every rep-facing status word comes from ONE classifier,
 * `../../dispatch-rep-status.ts`. Reps never see raw JSON, a `clay_*` word or
 * an HTTP status here.
 *
 * All state below is UI-only view state driven by the rep's clicks, never
 * seeded from query data, so `useDraftState` (CLAUDE.md "Draft State Under
 * SSE") does not apply.
 */

export interface DispatchInspectorProps {
  playId: string;
  /** For naming people in the "All people" table; `useLauncherMembers`'s own return. */
  members: readonly CampaignMember[];
  /** See the header comment's "OPEN-ON-SEND". */
  focusSendId?: string | null;
  /**
   * The test send to open by itself — the one a rep just started (its
   * `dispatchId` from the send receipt) or the one a "test already running"
   * refusal is about. A test has no send row to open by, so it has its own id.
   */
  focusTestDispatchId?: string | null;
  /**
   * "All people": clicking a person's name opens their side panel on that exact
   * row (`ReviewStep` writes `member-test-store`). Absent = names are plain text.
   */
  onOpenPerson?: (memberId: string, focus: MemberResultFocus) => void;
  /**
   * While the Review step shows a refused send's reason above the Send button, this says
   * so at the top of the log — otherwise "No sends yet." reads as "you never pressed anything".
   */
  lastRefusalNote?: string | null;
}

export function DispatchInspector({
  playId,
  members,
  focusSendId,
  focusTestDispatchId,
  onOpenPerson,
  lastRefusalNote = null,
}: DispatchInspectorProps) {
  const { apiFetch } = useApi();
  const logOpen = useReviewCardsStore((state) => state.logOpen);
  // View state, in a store only so the member panel's "All of Ana's sends" can switch it.
  const view = useDispatchLogFilterStore((state) => state.view);

  const [openSendIds, setOpenSendIds] = useState<ReadonlySet<string>>(new Set());
  useEffect(() => {
    if (!focusSendId) return;
    setOpenSendIds((prev) => (prev.has(focusSendId) ? prev : new Set(prev).add(focusSendId)));
  }, [focusSendId]);

  // Test dispatches (U7) have no send row / no `focusSendId` to open by — they
  // are collapsed by default, except the one `focusTestDispatchId` names (U0a):
  // the rep's own toggles are stored per card and win; with none, the named
  // card is open. Derived at render, so there is no effect to re-run and
  // nothing for a StrictMode double-invoke to double-apply.
  const [testToggles, setTestToggles] = useState<ReadonlyMap<string, boolean>>(new Map());
  const isTestOpen = (dispatchId: string) => testToggles.get(dispatchId) ?? dispatchId === focusTestDispatchId;

  const sendsQuery = useQuery({
    queryKey: playSendsQueryKey(playId),
    queryFn: () => listPlaySendsRequest(apiFetch, playId),
    refetchInterval: (query) => {
      const data = query.state.data as PlaybooksPlaySendsResponse | undefined;
      if (!data) return false;
      return anySendLive(data.sends, data.testSends) ? 5000 : false;
    },
  });

  // A send that could still change: the collapsed header says so, so closing the log never hides a running send.
  const liveSend = [...(sendsQuery.data?.sends ?? []), ...(sendsQuery.data?.testSends ?? [])].find(
    (send) => send.summary.live,
  );

  return (
    <div id={DISPATCH_LOG_ANCHOR_ID} data-review-card="Dispatch log">
      <CollapsibleCard
        open={logOpen}
        onOpenChange={setDispatchLogOpen}
        className="border-border-primary bg-bg-primary"
      >
        <CollapsibleCardHeader className="!items-center">
          <div
            className="flex min-w-0 flex-1 cursor-pointer flex-wrap items-center gap-2"
            onClick={() => setDispatchLogOpen(!logOpen)}
          >
            <Text size="sm" weight="semibold">
              Dispatch log
            </Text>
            {liveSend !== undefined && (
              <>
                <StatusBadge status="action">Sending</StatusBadge>
                {sendProgressLine(liveSend.summary) !== null && (
                  <Text size="xs" variant="tertiary" className="tabular-nums">
                    {sendProgressLine(liveSend.summary)}
                  </Text>
                )}
              </>
            )}
          </div>
        </CollapsibleCardHeader>
        <CollapsibleCardBody className="flex flex-col gap-3 p-4">
        {lastRefusalNote ? (
          <Text size="xs" variant="tertiary">
            {lastRefusalNote}
          </Text>
        ) : null}
        <SegmentedControl size="sm" value={view} onValueChange={(next) => setDispatchLogView(next)}>
          <SegmentedControlOption value="people">All people</SegmentedControlOption>
          <SegmentedControlOption value="sends">By send</SegmentedControlOption>
        </SegmentedControl>

        {sendsQuery.isLoading && (
          <div className="flex justify-center p-4">
            <LoadingSpinner size="sm" />
          </div>
        )}
        {sendsQuery.isError && (
          <Alert status="error">Couldn&apos;t load the send history. Try again.</Alert>
        )}
        {view === 'people' && sendsQuery.data && sendsQuery.data.sends.length + sendsQuery.data.testSends.length === 0 && (
          <Text size="xs" variant="tertiary">
            No sends yet.
          </Text>
        )}
        {view === 'people' && sendsQuery.data && sendsQuery.data.sends.length + sendsQuery.data.testSends.length > 0 && (
          <div className="rounded-md border border-border-secondary">
            <PlayLogTable
              playId={playId}
              sends={sendsQuery.data}
              live={anySendLive(sendsQuery.data.sends, sendsQuery.data.testSends)}
              members={members}
              onOpenPerson={onOpenPerson}
            />
          </div>
        )}
        {view === 'sends' && sendsQuery.data && sendsQuery.data.sends.length === 0 && (
          <Text size="xs" variant="tertiary">
            No sends yet.
          </Text>
        )}
        {view === 'sends' &&
          sendsQuery.data?.sends.map((send) => (
            <SendLogCard
              key={send.id}
              playId={playId}
              dispatchId={send.dispatchId}
              sentAt={send.sentAt}
              summary={send.summary}
              isTest={false}
              open={openSendIds.has(send.id)}
              onOpenChange={(open) =>
                setOpenSendIds((prev) => {
                  const next = new Set(prev);
                  if (open) next.add(send.id);
                  else next.delete(send.id);
                  return next;
                })
              }
            />
          ))}

        {view === 'sends' && sendsQuery.data && sendsQuery.data.testSends.length > 0 && (
          <div className="flex flex-col gap-2 border-t border-border-secondary pt-3">
            <Text size="xs" weight="medium" variant="tertiary">
              Test sends
            </Text>
            {sendsQuery.data.testSends.map((testSend) => (
              <SendLogCard
                key={testSend.dispatchId}
                playId={playId}
                dispatchId={testSend.dispatchId}
                sentAt={testSend.sentAt}
                summary={testSend.summary}
                isTest
                sampledFrom={testSend.routedCount}
                open={isTestOpen(testSend.dispatchId)}
                onOpenChange={(open) => setTestToggles((prev) => new Map(prev).set(testSend.dispatchId, open))}
              />
            ))}
          </div>
        )}
        </CollapsibleCardBody>
      </CollapsibleCard>
    </div>
  );
}
